/**
 * Bundles API client.
 *
 * Manages the SM4RT-only is_bundle flag on commercial products and the bundle
 * bill-of-materials (component licenses). Mirrors the pure-fetch service
 * pattern (relative /api/* URLs proxied to sm4rt-api; types from
 * @techgitter/contracts).
 */

import type {
  Bundle,
  BundleComponent,
  BundleListResponse,
} from '@techgitter/contracts';

const BASE = '/api/bundles';

async function readJson<T>(response: Response, op: string): Promise<T> {
  if (!response.ok) {
    let detail = '';
    try {
      detail = `: ${(await response.text()).slice(0, 200)}`;
    } catch {
      /* ignore */
    }
    throw new Error(`${op} failed (${response.status} ${response.statusText})${detail}`);
  }
  return (await response.json()) as T;
}

export async function fetchBundles(signal?: AbortSignal): Promise<Bundle[]> {
  const response = await fetch(BASE, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<BundleListResponse>(response, 'Load bundles');
  return body.items ?? [];
}

export async function fetchBundle(
  productId: string,
  signal?: AbortSignal,
): Promise<Bundle> {
  const response = await fetch(`${BASE}/${encodeURIComponent(productId)}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  return readJson<Bundle>(response, 'Load bundle');
}

/** Set/clear the SM4RT-only is_bundle flag on a commercial product. */
export async function setBundleFlag(
  productId: string,
  isBundle: boolean,
  signal?: AbortSignal,
): Promise<Bundle> {
  const response = await fetch(`${BASE}/${encodeURIComponent(productId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ isBundle }),
    signal,
  });
  return readJson<Bundle>(response, 'Update bundle flag');
}

/** Add a component license to a bundle. */
export async function addBundleComponent(
  bundleProductId: string,
  componentProductId: string,
  signal?: AbortSignal,
): Promise<BundleComponent> {
  const response = await fetch(
    `${BASE}/${encodeURIComponent(bundleProductId)}/components`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ componentProductId }),
      signal,
    },
  );
  return readJson<BundleComponent>(response, 'Add bundle component');
}

/** Remove a component license from a bundle. */
export async function removeBundleComponent(
  componentId: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${BASE}/components/${encodeURIComponent(componentId)}`,
    { method: 'DELETE', headers: { Accept: 'application/json' }, signal },
  );
  await readJson<{ deleted: boolean }>(response, 'Remove bundle component');
}
